import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import './BlogSection.css';

const BlogSection = ({ user, userRole, initialBlogs = null, limit = null }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [likedBlogs, setLikedBlogs] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [lastFetch, setLastFetch] = useState(null);

  useEffect(() => {
    // If initialBlogs supplied by parent (e.g., home popular blogs), use them and skip fetching
    if (initialBlogs && Array.isArray(initialBlogs)) {
      setBlogs(limit ? initialBlogs.slice(0, limit) : initialBlogs);
      // still load liked blogs for mentee so like buttons work
      if (user && user.id && userRole === 'mentee') loadLikedBlogs();
      return;
    }

    if (user && user.id) {
      loadBlogs();
      if (userRole === 'mentee') {
        loadLikedBlogs();
      }
    }
  }, [user, userRole, initialBlogs, limit]);

  const loadBlogs = async () => {
    if (!user || !user.id) {
      console.log('No user data available for loading blogs');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = userRole === 'mentor' 
        ? `${config.API_BASE_URL}/blogs/mentor/${user.id}`
        : `${config.API_BASE_URL}/blogs`;
      
      console.log('Loading blogs from URL:', url);
      console.log('User role:', userRole, 'User ID:', user.id);
      console.log('Token present:', !!token);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Blogs API response:', response.data);
      const blogsData = response.data.blogs || [];
      console.log('Setting blogs data:', blogsData.length, 'blogs');
      console.log('First blog sample:', blogsData[0]);
      setBlogs(blogsData);
    } catch (error) {
      console.error('Failed to load blogs:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const loadLikedBlogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/mentee/${user.id}/liked-blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikedBlogs(response.data.likedBlogs || []);
    } catch (error) {
      console.error('Failed to load liked blogs:', error);
    }
  };

  const handleLike = async (blogId) => {
    if (userRole !== 'mentee') return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${config.API_BASE_URL}/blogs/${blogId}/like`, {
        menteeId: user.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.liked) {
        setLikedBlogs([...likedBlogs, blogId]);
        setBlogs(blogs.map(blog => 
          blog.id === blogId ? { ...blog, likes_count: blog.likes_count + 1 } : blog
        ));
      } else {
        setLikedBlogs(likedBlogs.filter(id => id !== blogId));
        setBlogs(blogs.map(blog => 
          blog.id === blogId ? { ...blog, likes_count: blog.likes_count - 1 } : blog
        ));
      }
    } catch (error) {
      console.error('Failed to like blog:', error);
    }
  };

  const loadComments = async (blogId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_BASE_URL}/blogs/${blogId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_BASE_URL}/blogs/${selectedBlog.id}/comments`, {
        userId: user.id,
        content: newComment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewComment('');
      loadComments(selectedBlog.id);
      setBlogs(blogs.map(blog => 
        blog.id === selectedBlog.id ? { ...blog, comments_count: blog.comments_count + 1 } : blog
      ));
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleAddReply = async (parentCommentId) => {
    if (!replyText.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_BASE_URL}/blogs/${selectedBlog.id}/comments`, {
        userId: user.id,
        content: replyText,
        parentCommentId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReplyText('');
      setReplyingTo(null);
      loadComments(selectedBlog.id);
      setBlogs(blogs.map(blog => 
        blog.id === selectedBlog.id ? { ...blog, comments_count: blog.comments_count + 1 } : blog
      ));
    } catch (error) {
      console.error('Failed to add reply:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_BASE_URL}/comments/${commentId}`, {
        data: { userId: user.id, userRole },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      loadComments(selectedBlog.id);
      setBlogs(blogs.map(blog => 
        blog.id === selectedBlog.id ? { ...blog, comments_count: blog.comments_count - 1 } : blog
      ));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      console.log('Deleting blog:', {
        blogId,
        mentorId: user.id,
        selectedBlog: selectedBlog,
        userRole,
        token: token ? 'present' : 'missing'
      });
      
      const response = await axios.delete(`${config.API_BASE_URL}/blogs/${blogId}`, {
        data: { mentorId: user.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Delete response:', response.data);
      
      // Remove blog from list
      setBlogs(blogs.filter(blog => blog.id !== blogId));
      
      // Close modal
      closeBlogModal();
      
      alert('Blog deleted successfully!');
    } catch (error) {
      console.error('Failed to delete blog:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      const errorMessage = error.response?.data?.error || 'Failed to delete blog. Please try again.';
      alert(`Error: ${errorMessage}`);
    }
  };

  const openBlogModal = (blog) => {
    setSelectedBlog(blog);
    loadComments(blog.id);
  };

  const closeBlogModal = () => {
    setSelectedBlog(null);
    setComments([]);
    setNewComment('');
  };

  if (!user || !user.id) {
    return (
      <div className="blog-section">
        <div className="blog-header">
          <h2>Loading...</h2>
        </div>
        <div className="loading">Please wait while we load your data...</div>
      </div>
    );
  }

  return (
    <div className="blog-section">
      <div className="blog-header">
        <h2>{userRole === 'mentor' ? 'My Blogs' : 'Latest Blogs'}</h2>
        <button onClick={() => {
          // Clear cache and reload
          const cacheKey = `blogs_${userRole}_${user.id}`;
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(`${cacheKey}_time`);
          setLastFetch(null);
          loadBlogs();
        }} className="refresh-btn">🔄 Refresh</button>
      </div>

      {loading ? (
        <div className="loading">Loading blogs...</div>
      ) : (
        <div className="blogs-grid">
          {blogs.map(blog => (
            <div key={blog.id} className="blog-card" onClick={() => openBlogModal(blog)}>
              {blog.images && blog.images.length > 0 && (
                <div className="blog-image">
                  <img src={blog.images[0]} alt={blog.title} />
                </div>
              )}
              <div className="blog-content">
                <h3>{blog.title}</h3>
                <p className="blog-description">{blog.description}</p>
                <div className="blog-meta">
                  <span className="blog-author">By {blog.mentor_name}</span>
                  <span className="blog-date">{new Date(blog.created_at).toLocaleDateString()}</span>
                </div>
                <div className="blog-stats">
                  <span>❤️ {blog.likes_count}</span>
                  <span>💬 {blog.comments_count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedBlog && (
        <div className="blog-modal" onClick={closeBlogModal}>
          <div className="blog-modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={closeBlogModal}>×</button>
            
            <div className="blog-header-modal">
              <h2>{selectedBlog.title}</h2>
              <div className="blog-meta">
                <span>By {selectedBlog.mentor_name}</span>
                <span>{new Date(selectedBlog.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {selectedBlog.images && selectedBlog.images.length > 0 && (
              <div className="blog-images">
                {selectedBlog.images.map((img, index) => (
                  <img key={index} src={img} alt={`Blog image ${index + 1}`} />
                ))}
              </div>
            )}

            <div className="blog-content-modal">
              <p>{selectedBlog.content}</p>
            </div>

            <div className="blog-actions">
              {userRole === 'mentee' && (
                <button 
                  className={`like-btn ${likedBlogs.includes(selectedBlog.id) ? 'liked' : ''}`}
                  onClick={() => handleLike(selectedBlog.id)}
                >
                  {likedBlogs.includes(selectedBlog.id) ? '❤️ Liked' : '🤍 Like'}
                </button>
              )}
              {userRole === 'mentor' && (
                <div className="mentor-actions">
                  <button 
                    className="delete-btn"
                    onClick={() => {
                      console.log('Delete button clicked:', {
                        blogId: selectedBlog.id,
                        blogMentorId: selectedBlog.mentor_id,
                        currentUserId: user.id,
                        userRole,
                        selectedBlog
                      });
                      handleDeleteBlog(selectedBlog.id);
                    }}
                    style={{ 
                      backgroundColor: '#dc3545', 
                      color: 'white', 
                      border: 'none', 
                      padding: '8px 16px', 
                      borderRadius: '6px', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      zIndex: 1000,
                      position: 'relative'
                    }}
                  >
                    🗑️ Delete Blog
                  </button>
                  <small style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>
                    Blog ID: {selectedBlog.id} | Owner: {selectedBlog.mentor_id} | You: {user.id}
                  </small>
                </div>
              )}

              <span className="stats">❤️ {selectedBlog.likes_count} 💬 {selectedBlog.comments_count}</span>
            </div>

            <div className="comments-section">
              <h3>Comments</h3>
              
              <div className="add-comment">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  rows="3"
                />
                <button onClick={handleAddComment}>Post Comment</button>
              </div>

              <div className="comments-list">
                {comments.filter(comment => !comment.parent_comment_id).map(comment => (
                  <div key={comment.id} className="comment">
                    <div className="comment-header">
                      <img 
                        src={comment.author_avatar || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiNFMUU1RTkiLz4KPHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeD0iMTAiIHk9IjEwIj4KPHBhdGggZD0iTTEyIDEyQzE0LjIwOTEgMTIgMTYgMTAuMjA5MSAxNiA4QzE2IDUuNzkwODYgMTQuMjA5MSA0IDEyIDRDOS43OTA4NiA0IDggNS43OTA4NiA4IDhDOCAxMC4yMDkxIDkuNzkwODYgMTIgMTIgMTJaIiBmaWxsPSIjNjY3RUVBIi8+CjxwYXRoIGQ9Ik0xMiAxNEM5LjMzIDEzLjk5IDcuMDEgMTUuNjIgNiAxOEMxMC4wMSAyMCAxMy45OSAyMCAxOCAxOEMxNi45OSAxNS42MiAxNC42NyAxMy45OSAxMiAxNFoiIGZpbGw9IiM2NjdFRUEiLz4KPHN2Zz4KPHN2Zz4='} 
                        alt={comment.author_name} 
                        className="comment-avatar"
                      />
                      <div className="comment-info">
                        <span className="comment-author">{comment.author_name}</span>
                        <span className="comment-date">{new Date(comment.created_at).toLocaleDateString()}</span>
                      </div>
                      {(comment.user_id === user.id || (userRole === 'mentor' && selectedBlog.mentor_id === user.id)) && (
                        <button 
                          className="delete-comment-btn"
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                    <p className="comment-content">{comment.content}</p>
                    
                    {userRole === 'mentor' && selectedBlog.mentor_id === user.id && (
                      <button 
                        className="reply-btn"
                        onClick={() => setReplyingTo(comment.id)}
                      >
                        💬 Reply
                      </button>
                    )}
                    
                    {replyingTo === comment.id && (
                      <div className="reply-form">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write a reply..."
                          rows="2"
                        />
                        <div className="reply-actions">
                          <button onClick={() => handleAddReply(comment.id)}>Reply</button>
                          <button onClick={() => { setReplyingTo(null); setReplyText(''); }}>Cancel</button>
                        </div>
                      </div>
                    )}
                    
                    {/* Show replies */}
                    <div className="replies">
                      {comments.filter(reply => reply.parent_comment_id === comment.id).map(reply => (
                        <div key={reply.id} className="reply">
                          <div className="comment-header">
                            <img 
                              src={reply.author_avatar || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMTIiIGZpbGw9IiNFMUU1RTkiLz4KPHN2ZyB3aWR0aD0iOCIgaGVpZ2h0PSI4IiB2aWV3Qm94PSIwIDAgMjQgMjQiIGZpbGw9Im5vbmUiIHg9IjgiIHk9IjgiPgo8cGF0aCBkPSJNMTIgMTJDMTQuMjA5MSAxMiAxNiAxMC4yMDkxIDE2IDhDMTYgNS43OTA4NiAxNC4yMDkxIDQgMTIgNEM5Ljc5MDg2IDQgOCA1Ljc5MDg2IDggOEM4IDEwLjIwOTEgOS43OTA4NiAxMiAxMiAxMloiIGZpbGw9IiM2NjdFRUEiLz4KPHBhdGggZD0iTTEyIDE0QzkuMzMgMTMuOTkgNy4wMSAxNS42MiA2IDE4QzEwLjAxIDIwIDEzLjk5IDIwIDE4IDE4QzE2Ljk5IDE1LjYyIDE0LjY3IDEzLjk5IDEyIDE0WiIgZmlsbD0iIzY2N0VFQSIvPgo8L3N2Zz4KPHN2Zz4='} 
                              alt={reply.author_name} 
                              className="reply-avatar"
                            />
                            <div className="comment-info">
                              <span className="comment-author">{reply.author_name}</span>
                              <span className="comment-date">{new Date(reply.created_at).toLocaleDateString()}</span>
                            </div>
                            {(reply.user_id === user.id || (userRole === 'mentor' && selectedBlog.mentor_id === user.id)) && (
                              <button 
                                className="delete-comment-btn"
                                onClick={() => handleDeleteComment(reply.id)}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                          <p className="comment-content">{reply.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogSection;
